import Common from '@/libs/common';

/** A stop or a line the passenger opened: its name is resolved live from the GTFS. */
export interface RecentGtfsSearch {
  id: string;
  type: 'route' | 'stop';
}

/** A postal address picked for an itinerary: outside the GTFS, it keeps its own name and place. */
export interface RecentAddressSearch {
  id: string;
  type: 'address';
  name: string;
  locality: string;
  lat: number;
  lon: number;
}

export type RecentSearch = RecentGtfsSearch | RecentAddressSearch;

export const RECENT_SEARCHES_LIMIT = 10;

const STORAGE_VERSION = 1;

interface StoredRecentSearches {
  items: RecentSearch[];
  v: number;
}

export function recentSearchesStorageKey(groupId: string): string {
  return `recentSearches.${groupId}`;
}

function isRecentSearch(value: unknown): value is RecentSearch {
  if (typeof value !== 'object' || value === null) return false;
  const { id, type } = value as Partial<RecentSearch>;
  if (typeof id !== 'string') return false;
  if (type === 'route' || type === 'stop') return true;
  if (type !== 'address') return false;
  const { name, locality, lat, lon } = value as Partial<RecentAddressSearch>;
  return (
    typeof name === 'string' && typeof locality === 'string' && Number.isFinite(lat) && Number.isFinite(lon)
  );
}

export function readRecentSearches(groupId: string): RecentSearch[] {
  const stored: StoredRecentSearches | null = Common.safeLSParse(recentSearchesStorageKey(groupId));
  if (!stored || stored.v !== STORAGE_VERSION || !Array.isArray(stored.items)) return [];
  return stored.items.filter(isRecentSearch).slice(0, RECENT_SEARCHES_LIMIT);
}

export function writeRecentSearches(groupId: string, items: RecentSearch[]): void {
  const stored: StoredRecentSearches = { items, v: STORAGE_VERSION };
  try {
    localStorage.setItem(recentSearchesStorageKey(groupId), JSON.stringify(stored));
  } catch {
    // Accepted (private browsing, full quota): the history only lives in memory for this session.
  }
}

function isSameRecentSearch(a: RecentSearch, b: RecentSearch): boolean {
  return a.type === b.type && a.id === b.id;
}

/** Most recent first, without duplicates, capped to the last few entries. */
export function pushRecentSearch(items: RecentSearch[], entry: RecentSearch): RecentSearch[] {
  return [entry, ...items.filter(item => !isSameRecentSearch(item, entry))].slice(0, RECENT_SEARCHES_LIMIT);
}

/** Still in the GTFS loaded: a line or a stop it lost goes, an address stays, being outside it. */
export function isKnownRecentSearch(
  entry: RecentSearch,
  gtfs: { routes: Record<string, unknown>; stops: Record<string, unknown> },
): boolean {
  if (entry.type === 'address') return true;
  return entry.id in (entry.type === 'route' ? gtfs.routes : gtfs.stops);
}
